import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getSuperAdminPerformance } from "../../service/api";
import {
  Card, Kpi, Pill, PeriodPicker, PRESETS, MiniBar, useSort,
  fmtNum, fmtMoney, fmtMoneyObj, fmtPct, fmtDays, fmtDate, bandPill, isSuperAdmin, Forbidden, Loading
} from "./ui";
import { WorkloadBar, ScoreTrend } from "./charts";

const PART_LABELS = {
  throughput: ["Throughput", "Interviews coordinated compared with the team average. 1.5× the average scores full marks."],
  speed: ["Speed", "Median days from candidate creation to the interview being scheduled. 1 day scores full marks, 5 days scores zero."],
  quality: ["Quality", "Cancellations, no-shows and reschedules on this admin's interviews. Lower is better."],
  service: ["Service", "Share of this admin's tickets closed within 48 hours."],
  value: ["Value", "Gross margin managed per rupee of salary for the period. 2× salary scores full marks."]
};

export default function AdminPerformance() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(PRESETS[1].range());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getSuperAdminPerformance(range)
      .then((res) => setData(res?.data || null))
      .catch((err) => { console.error(err); toast.error(err?.response?.data?.message || "Could not load performance data"); })
      .finally(() => setLoading(false));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  if (!isSuperAdmin()) return <Forbidden />;

  const k = data?.kpis || {};
  const w = data?.weights || {};
  const avg = data?.kpis?.interviewsPerAdmin || 0;

  return (
    <div className="intel-page">
      <div className="intel-crumbs"><Link to="/admin/intelligence">Business Intelligence</Link><span>›</span><span className="strong" style={{ color: "#3A3A3A" }}>Admin team performance</span></div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 className="intel-title">Admin team performance <Pill tone="solid">Super admin only</Pill></h1>
          <p className="intel-subtitle">Admin users are PANLS employees (interview coordinators). Each admin is measured on the interviews, candidates and tickets assigned to them. Salary is entered by the super admin only.</p>
        </div>
        <button className="intel-btn secondary" type="button" onClick={() => navigate("/admin/intelligence/salaries")}>Salary settings</button>
      </div>

      <div className="intel-toolbar">
        <PeriodPicker preset={preset} setPreset={setPreset} range={range} setRange={setRange} />
        <div className="spacer" />
        {data && <span className="intel-updated">{fmtDate(data.period.startDate)} – {fmtDate(data.period.endDate)}</span>}
        <button className="intel-btn secondary small" type="button" onClick={load} disabled={loading}>Refresh</button>
      </div>

      {loading && !data ? <Loading /> : !data ? null : (
        <>
          <div className="intel-grid cols-6">
            <Kpi label="Admin users" value={fmtNum(k.admins)} sub={`${fmtNum(k.interviewsPerAdmin)} interviews per admin`} help="Active users with profile type ADMIN." />
            <Kpi label="Team salary (period)" value={fmtMoney(k.totalSalary)} sub={k.costPerInterview ? `${fmtMoney(k.costPerInterview, "INR", false)} per interview coordinated` : "Set salaries to see cost"} help="Monthly salaries scaled to the period length." />
            <Kpi label="Margin managed" value={fmtMoney(k.totalMarginInr)} valueTone={k.totalMarginInr < 0 ? "bad" : "good"} sub={k.valueRatio !== null ? `${k.valueRatio}× salary cost` : "–"} tone={k.valueRatio !== null && k.valueRatio < 1.2 ? "warn" : "good"} help={`Revenue billed minus panel cost on interviews coordinated by admins. USD folded in at ${data.fxUsdInr} INR.`} />
            <Kpi label="Median time to schedule" value={fmtDays(k.medianDaysToSchedule)} sub="Candidate created → slot confirmed" />
            <Kpi label="Tickets in SLA" value={fmtPct(k.ticketSlaPct, 0)} sub="Closed within 48 hours" />
            <Kpi label="Average score" value={fmtNum(k.avgScore)} valueTone={k.avgScore >= 75 ? "good" : ""} sub="Target 75" />
          </div>

          {data.notes?.length > 0 && (
            <Card title="What stands out" hint="Rule-based observations. No AI is used.">
              <div className="intel-card-body"><ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.8 }}>{data.notes.map((n, i) => <li key={i}>{n}</li>)}</ul></div>
            </Card>
          )}

          <Scorecard scorecard={data.scorecard} />

          <div className="intel-grid one-two">
            <Card title="How the score is built" hint="0 to 100">
              <div className="intel-card-body">
                <div className="weights">
                  {Object.keys(PART_LABELS).map((p) => (
                    <div key={p}><span><span className="strong">{PART_LABELS[p][0]}</span> <span className="muted">· {PART_LABELS[p][1]}</span></span><span className="strong" style={{ whiteSpace: "nowrap" }}>{Math.round((w[p] || 0) * 100)}%</span></div>
                  ))}
                </div>
                <div className="intel-note" style={{ borderTop: "1px solid #E8E8E8", marginTop: 12, paddingTop: 10 }}>Bands: 80 and above exceeds, 65 to 79 meets, below 65 needs support. Where an admin has no tickets or no salary set, that part uses a neutral default so nobody is penalised for missing data.</div>
              </div>
            </Card>
            <div>
              <Card title="Workload this period" hint={`Interviews coordinated · team average ${fmtNum(avg)} · orange = more than 1.5× the average`}>
                <div className="intel-card-body"><WorkloadBar scorecard={data.scorecard} average={avg} /></div>
              </Card>
              <Card title="Team score, last 6 months" hint="Average score across admins, target 75">
                <div className="intel-card-body"><ScoreTrend trend={data.trend} /></div>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Scorecard({ scorecard }) {
  const { sorted, Th } = useSort(scorecard, "score");
  const [open, setOpen] = useState(null);
  const totals = (scorecard || []).reduce((a, m) => ({
    interviews: a.interviews + m.interviews, salary: a.salary + m.salaryForPeriod, margin: a.margin + m.marginManagedInr,
    tickets: a.tickets + m.ticketsClosed, sla: a.sla + m.ticketsInSla, priced: a.priced + (m.pricedInterviews || 0), unpriced: a.unpriced + (m.unpricedInterviews || 0)
  }), { interviews: 0, salary: 0, margin: 0, tickets: 0, sla: 0, priced: 0, unpriced: 0 });
  const days = (d) => (d === null || d === undefined ? "–" : d < 1 ? "< 1 d" : fmtDays(d));
  const scoreColor = (b) => (b === "EXCEEDS" ? "#2E8B57" : b === "MEETS" ? "#3E67B1" : "#D9822B");
  return (
    <Card title="Admin scorecard" hint="Click a row to see the score breakdown" superOnly>
      <div className="intel-scroll">
        <table className="intel-table compact-table">
          <thead>
            <tr>
              <Th k="name">Admin</Th>
              <Th k="interviews" num>Interviews</Th>
              <Th k="medianDaysToSchedule" num>Time to schedule</Th>
              <Th k="cancelLikeRate" num>Cancel / no-show · reschedules</Th>
              <Th k="pendingFeedback" num>Pending feedback</Th>
              <Th k="ticketSlaPct" num>Tickets in SLA</Th>
              <Th k="marginManagedInr" num>Margin managed · value ratio</Th>
              <Th k="score" num>Score</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((m) => (
              <React.Fragment key={m.userId}>
                <tr className="clickable" onClick={() => setOpen(open === m.userId ? null : m.userId)}>
                  <td className="wrap" style={{ minWidth: 170 }}>
                    <div className="strong">{m.name}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{m.roleName || "Admin"}{m.joined ? ` · joined ${fmtDate(m.joined)}` : ""}</div>
                  </td>
                  <td className="num">
                    <div className="strong">{fmtNum(m.interviews)}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{fmtNum(m.companies)} compan{m.companies === 1 ? "y" : "ies"}</div>
                  </td>
                  <td className={`num ${m.medianDaysToSchedule !== null && m.medianDaysToSchedule > 3 ? "warn strong" : ""}`}>{days(m.medianDaysToSchedule)}</td>
                  <td className="num">
                    <div className={m.cancelLikeRate >= 15 ? "bad strong" : ""}>{fmtNum(m.cancelLike)} <span className="muted">· {fmtPct(m.cancelLikeRate, 0)}</span></div>
                    <div className={`${m.interviews && m.reschedules / m.interviews >= 0.2 ? "bad strong" : "muted"}`} style={{ fontSize: 11 }}>{fmtNum(m.reschedules)} rescheduled</div>
                  </td>
                  <td className={`num ${m.pendingFeedback >= 5 ? "warn strong" : ""}`}>{fmtNum(m.pendingFeedback)}</td>
                  <td className="num">{m.ticketSlaPct === null ? <span className="muted" title="No tickets assigned in this period">–</span> : fmtPct(m.ticketSlaPct, 0)}</td>
                  <td className="num">
                    {m.pricedInterviews > 0 ? (
                      <>
                        <div className={`strong ${m.marginManagedInr < 0 ? "bad" : ""}`}>{fmtMoney(m.marginManagedInr)}{(m.margin?.USD || 0) !== 0 && <span className="muted" style={{ fontSize: 11 }}> (incl. {fmtMoney(m.margin.USD, "USD")})</span>}</div>
                        <div style={{ fontSize: 11 }}>
                          {m.valueRatio === null ? <span className="muted" title="Set this admin's salary in Salary settings">salary not set</span> : <span className={m.valueRatio < 1 ? "warn strong" : "good strong"}>{m.valueRatio}× salary</span>}
                          {m.unpricedInterviews > 0 && <span className="muted"> · {m.unpricedInterviews} unpriced</span>}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="muted">not priced yet</div>
                        <div className="muted" style={{ fontSize: 11 }} title="These customers have no rate card on their ticket">{m.interviews ? `${fmtNum(m.unpricedInterviews)} interviews without a rate card` : "no interviews"}</div>
                      </>
                    )}
                  </td>
                  <td className="num" style={{ whiteSpace: "nowrap" }}><MiniBar pct={m.score} color={scoreColor(m.band)} /><span className="strong">{m.score}</span> {bandPill(m.band)}</td>
                </tr>
                {open === m.userId && (
                  <tr>
                    <td colSpan={8} style={{ background: "#F8FAFF", whiteSpace: "normal" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12 }}>
                        {Object.keys(PART_LABELS).map((p) => (
                          <div key={p} style={{ background: "#FFF", border: "1px solid #E8E8E8", borderRadius: 4, padding: "10px 12px" }}>
                            <div className="bar-row"><span className="strong">{PART_LABELS[p][0]}</span><span className="strong">{m.parts?.[p]}/100</span></div>
                            <div className="bar-track"><div className="bar-fill" style={{ width: `${m.parts?.[p] || 0}%`, background: "#3E67B1" }} /></div>
                            <div className="intel-note" style={{ marginTop: 6 }}>{p === "value" && !(m.pricedInterviews > 0) ? "Neutral default: none of this admin's interviews have a customer rate card yet." : p === "service" && m.ticketSlaPct === null ? "Neutral default: no tickets in this period." : PART_LABELS[p][1]}</div>
                          </div>
                        ))}
                      </div>
                      <div className="intel-note" style={{ marginTop: 10 }}>Revenue {fmtMoneyObj(m.revenue)} · panel cost {fmtMoneyObj(m.panelCost)} · tickets {fmtNum(m.tickets)} (closed {fmtNum(m.ticketsClosed)}, in SLA {fmtNum(m.ticketsInSla)}) · completed {fmtNum(m.completed)}{m.hasSalary ? ` · salary for period ${fmtMoney(m.salaryForPeriod)}` : " · salary not set"}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!sorted.length && <tr><td colSpan={8} className="intel-empty">No active admin users found.</td></tr>}
          </tbody>
          {sorted.length > 0 && (
            <tfoot>
              <tr>
                <td>Team</td>
                <td className="num">{fmtNum(totals.interviews)}</td>
                <td className="num" /><td className="num" /><td className="num" />
                <td className="num">{totals.tickets ? fmtPct((totals.sla / totals.tickets) * 100, 0) : "–"}</td>
                <td className="num">{totals.priced > 0 ? <>{fmtMoney(totals.margin)}{totals.salary ? <span className="muted" style={{ fontSize: 11 }}> · {(totals.margin / totals.salary).toFixed(2)}× salary</span> : null}</> : <span className="muted">not priced yet</span>}</td>
                <td className="num" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}
